import React, { useState } from 'react';
import { apiFetch } from './lib/api';

// ==================== TYPES ====================
type RecheckStatus = 'PASSED' | 'FAILED';

interface FailureReason {
	code: string;
	message: string;
}

interface ObligationChanges {
	previousTotal: number;
	currentTotal: number;
	changePercentage: number;
}

interface ApplicantRecheckResult {
	nationalId: string;
	firstName: string;
	lastName: string;
	recheckStatus: RecheckStatus;
	failureReason?: FailureReason;
	obligationChanges?: ObligationChanges;
}

interface RecheckResponseData {
	applicationId: string;
	recheckStatus: RecheckStatus;
	recheckTimestamp: string;
	applicantResults: ApplicantRecheckResult[];
}

interface ApiError {
	code: string;
	message: string;
}

interface ApiResponse {
	success: boolean;
	data?: RecheckResponseData;
	message?: string;
	errors?: ApiError[];
}

// ==================== MOCK APPLICATION IDS ====================
const MOCK_IDS = [
	{ id: '11111111-1111-1111-1111-111111111111', label: 'Sole applicant — PASSED' },
	{ id: '22222222-2222-2222-2222-222222222222', label: 'Joint applicants — all PASSED' },
	{ id: '33333333-3333-3333-3333-333333333333', label: 'Sole applicant — FAILED' },
	{ id: '44444444-4444-4444-4444-444444444444', label: 'Joint applicants — all FAILED' },
	{ id: '55555555-5555-5555-5555-555555555555', label: 'Joint — primary FAILED, co-applicant PASSED' },
	{ id: '66666666-6666-6666-6666-666666666666', label: 'Joint — primary PASSED, co-applicant FAILED' },
];

// ==================== KNOWN FAILURE CODES ====================
const FAILURE_CODE_DESCRIPTIONS: Record<string, string> = {
	OBLIGATIONS_INCREASED: 'Monthly credit obligations have increased since original application',
	NEW_CREDIT_INSTRUMENT: 'New credit instrument detected since original application',
	NEW_LOAN_DETECTED: 'New loan detected that affects repayment capacity',
	SIMULATED_FAILURE: 'Failure was simulated via the simulateFailure query parameter',
};

// ==================== UTILITY FUNCTIONS ====================
const formatCurrency = (amount: number): string => {
	return new Intl.NumberFormat('en-SA', {
		style: 'currency',
		currency: 'SAR',
		minimumFractionDigits: 0,
		maximumFractionDigits: 0,
	}).format(amount);
};

const formatTimestamp = (iso: string): string => {
	const date = new Date(iso);
	return date.toLocaleString('en-US', {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
		second: '2-digit',
		timeZoneName: 'short',
	});
};

// ==================== SUB-COMPONENTS ====================

const StatusBadge: React.FC<{ status: RecheckStatus; size?: 'sm' | 'lg' }> = ({ status, size = 'sm' }) => {
	const passed = status === 'PASSED';
	const sizeClasses = size === 'lg' ? 'px-4 py-2 text-base' : 'px-2.5 py-0.5 text-xs';
	return (
		<span
			className={`inline-flex items-center font-semibold rounded-full ${sizeClasses} ${
				passed ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
			}`}>
			{passed ? '✓' : '✗'} {status}
		</span>
	);
};

const ObligationChangesCard: React.FC<{ changes: ObligationChanges }> = ({ changes }) => {
	const increased = changes.changePercentage > 0;
	return (
		<div className="mt-3 bg-gray-50 rounded-lg p-4 border border-gray-200">
			<h5 className="text-sm font-semibold text-gray-700 mb-3">Obligation Changes</h5>
			<div className="grid grid-cols-3 gap-4">
				<div>
					<p className="text-xs text-gray-500 mb-1">Previous Total</p>
					<p className="text-sm font-medium text-gray-900">{formatCurrency(changes.previousTotal)}</p>
				</div>
				<div>
					<p className="text-xs text-gray-500 mb-1">Current Total</p>
					<p className="text-sm font-medium text-gray-900">{formatCurrency(changes.currentTotal)}</p>
				</div>
				<div>
					<p className="text-xs text-gray-500 mb-1">Change</p>
					<p className={`text-sm font-bold ${increased ? 'text-red-600' : 'text-green-600'}`}>
						{increased ? '↑' : '↓'} {Math.abs(changes.changePercentage)}%
					</p>
				</div>
			</div>
			{/* Visual bar comparison */}
			<div className="mt-3 space-y-1.5">
				<div className="flex items-center gap-2">
					<span className="text-xs text-gray-500 w-16">Previous</span>
					<div className="flex-1 bg-gray-200 rounded-full h-2.5">
						<div
							className="bg-blue-400 h-2.5 rounded-full transition-all"
							style={{
								width: `${Math.min(100, (changes.previousTotal / Math.max(changes.previousTotal, changes.currentTotal)) * 100)}%`,
							}}
						/>
					</div>
				</div>
				<div className="flex items-center gap-2">
					<span className="text-xs text-gray-500 w-16">Current</span>
					<div className="flex-1 bg-gray-200 rounded-full h-2.5">
						<div
							className={`h-2.5 rounded-full transition-all ${increased ? 'bg-red-400' : 'bg-green-400'}`}
							style={{
								width: `${Math.min(100, (changes.currentTotal / Math.max(changes.previousTotal, changes.currentTotal)) * 100)}%`,
							}}
						/>
					</div>
				</div>
			</div>
		</div>
	);
};

const ApplicantResultCard: React.FC<{ result: ApplicantRecheckResult; index: number }> = ({ result, index }) => {
	const passed = result.recheckStatus === 'PASSED';
	return (
		<div className={`rounded-lg border-2 p-4 ${passed ? 'border-green-200 bg-green-50/30' : 'border-red-200 bg-red-50/30'}`}>
			<div className="flex items-start justify-between mb-3">
				<div>
					<p className="text-sm text-gray-500">Applicant {index + 1}</p>
					<p className="text-lg font-semibold text-gray-900">
						{result.firstName} {result.lastName}
					</p>
					<p className="text-sm text-gray-500 font-mono">ID: {result.nationalId}</p>
				</div>
				<StatusBadge status={result.recheckStatus} />
			</div>

			{result.failureReason && (
				<div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3">
					<div className="flex items-start gap-2">
						<span className="text-red-500 mt-0.5 text-sm">&#9888;</span>
						<div>
							<p className="text-sm font-medium text-red-800">
								{FAILURE_CODE_DESCRIPTIONS[result.failureReason.code] || result.failureReason.message}
							</p>
							<p className="text-xs text-red-600 mt-1 font-mono">Code: {result.failureReason.code}</p>
						</div>
					</div>
				</div>
			)}

			{result.obligationChanges && <ObligationChangesCard changes={result.obligationChanges} />}
		</div>
	);
};

const RecheckResults: React.FC<{ data: RecheckResponseData }> = ({ data }) => {
	const [showRawJson, setShowRawJson] = useState(false);
	const passed = data.recheckStatus === 'PASSED';
	const passedCount = data.applicantResults.filter((r) => r.recheckStatus === 'PASSED').length;
	const failedCount = data.applicantResults.filter((r) => r.recheckStatus === 'FAILED').length;

	return (
		<div className="space-y-4">
			{/* Overall Status Card */}
			<div className={`rounded-lg border-2 p-6 ${passed ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50'}`}>
				<div className="flex items-center justify-between">
					<div>
						<h3 className="text-lg font-semibold text-gray-800">Overall Recheck Result</h3>
						<p className="text-sm text-gray-500 mt-1">Application: {data.applicationId}</p>
						<p className="text-sm text-gray-500">Performed: {formatTimestamp(data.recheckTimestamp)}</p>
					</div>
					<StatusBadge status={data.recheckStatus} size="lg" />
				</div>
				<div className="mt-4 flex items-center gap-4">
					<div className="flex items-center gap-1.5">
						<span className="w-3 h-3 rounded-full bg-green-400 inline-block" />
						<span className="text-sm text-gray-600">
							{passedCount} passed
						</span>
					</div>
					{failedCount > 0 && (
						<div className="flex items-center gap-1.5">
							<span className="w-3 h-3 rounded-full bg-red-400 inline-block" />
							<span className="text-sm text-gray-600">
								{failedCount} failed
							</span>
						</div>
					)}
					<span className="text-sm text-gray-400">|</span>
					<span className="text-sm text-gray-600">
						{data.applicantResults.length} total applicant{data.applicantResults.length !== 1 ? 's' : ''}
					</span>
				</div>
			</div>

			{/* Individual Applicant Results */}
			<div>
				<h3 className="text-lg font-semibold text-gray-800 mb-3">Applicant Results</h3>
				<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
					{data.applicantResults.map((result, index) => (
						<ApplicantResultCard key={result.nationalId} result={result} index={index} />
					))}
				</div>
			</div>

			{/* Raw JSON Viewer */}
			<div className="bg-white rounded-lg border shadow-sm">
				<button
					onClick={() => setShowRawJson(!showRawJson)}
					className="w-full px-4 py-3 flex items-center justify-between text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
					<span>Raw JSON Response</span>
					<span className="text-gray-400">{showRawJson ? '▲' : '▼'}</span>
				</button>
				{showRawJson && (
					<div className="px-4 pb-4">
						<pre className="bg-gray-900 text-green-400 p-4 rounded-lg text-xs overflow-auto max-h-96 font-mono">
							{JSON.stringify({ success: true, data }, null, 2)}
						</pre>
					</div>
				)}
			</div>
		</div>
	);
};

const ErrorDisplay: React.FC<{ message: string; errors?: ApiError[]; statusCode?: number }> = ({
	message,
	errors,
	statusCode,
}) => (
	<div className="bg-red-50 border-2 border-red-300 rounded-lg p-6">
		<div className="flex items-start gap-3">
			<span className="text-red-500 text-2xl">&#9888;</span>
			<div className="flex-1">
				<h3 className="text-lg font-semibold text-red-800">
					{statusCode === 404 ? 'Application Not Found' : 'Error'}
					{statusCode && <span className="ml-2 text-sm font-normal text-red-600">({statusCode})</span>}
				</h3>
				<p className="text-sm text-red-700 mt-1">{message}</p>
				{errors && errors.length > 0 && (
					<div className="mt-3 space-y-2">
						{errors.map((err, i) => (
							<div key={i} className="bg-red-100 rounded p-2">
								<p className="text-xs font-mono text-red-600">Code: {err.code}</p>
								<p className="text-sm text-red-800">{err.message}</p>
							</div>
						))}
					</div>
				)}
			</div>
		</div>
	</div>
);

// ==================== MAIN COMPONENT ====================
interface CreditBureauRecheckProps {
	baseUrl: string;
}

const CreditBureauRecheck: React.FC<CreditBureauRecheckProps> = ({ baseUrl }) => {
	// Form state
	const [applicationId, setApplicationId] = useState('');
	const [simulateFailure, setSimulateFailure] = useState(false);
	const [inputMode, setInputMode] = useState<'manual' | 'mock'>('manual');

	// Request state
	const [isLoading, setIsLoading] = useState(false);
	const [response, setResponse] = useState<ApiResponse | null>(null);
	const [httpStatus, setHttpStatus] = useState<number | null>(null);
	const [error, setError] = useState('');

	const isValidUUID = (id: string): boolean => {
		return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
	};

	const handleSubmit = async () => {
		// Validation
		if (!applicationId.trim()) {
			setError('Application ID is required');
			return;
		}
		if (!isValidUUID(applicationId.trim())) {
			setError('Application ID must be a valid UUID (e.g., 550e8400-e29b-41d4-a716-446655440000)');
			return;
		}

		setIsLoading(true);
		setResponse(null);
		setHttpStatus(null);
		setError('');

		try {
			const url = new URL(`${baseUrl}/api/v1/application/${applicationId.trim()}/recheck`);
			if (simulateFailure) {
				url.searchParams.set('simulateFailure', 'true');
			}

			const res = await apiFetch(url.toString(), {
				method: 'POST',
			});

			setHttpStatus(res.status);
			const data: ApiResponse = await res.json();
			setResponse(data);
		} catch (err) {
			setError(err instanceof Error ? err.message : 'An unexpected error occurred while calling the API');
		} finally {
			setIsLoading(false);
		}
	};

	const handleReset = () => {
		setApplicationId('');
		setSimulateFailure(false);
		setResponse(null);
		setHttpStatus(null);
		setError('');
	};

	const handleMockSelect = (id: string) => {
		setApplicationId(id);
	};

	return (
		<div className="space-y-6">
			{/* Request Form */}
			<div className="bg-white p-6 rounded-lg shadow-sm border">
				<div className="flex items-center justify-between mb-4">
					<h3 className="text-lg font-semibold text-gray-800">Credit Bureau Recheck</h3>
					<span className="text-xs font-mono bg-blue-100 text-blue-700 px-2 py-1 rounded">
						POST /api/v1/application/:applicationId/recheck
					</span>
				</div>

				<p className="text-sm text-gray-600 mb-6">
					Recheck credit bureau data for a loan application after contract signing. Validates that credit
					obligations have not changed significantly by performing a SIMAH enquiry for all applicants.
				</p>

				{/* Input Mode Toggle */}
				<div className="flex items-center gap-2 mb-4">
					<button
						onClick={() => setInputMode('manual')}
						className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
							inputMode === 'manual'
								? 'bg-blue-600 text-white'
								: 'bg-gray-100 text-gray-600 hover:bg-gray-200'
						}`}>
						Manual Input
					</button>
					<button
						onClick={() => setInputMode('mock')}
						className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
							inputMode === 'mock'
								? 'bg-blue-600 text-white'
								: 'bg-gray-100 text-gray-600 hover:bg-gray-200'
						}`}>
						Mock IDs
					</button>
				</div>

				{/* Application ID Input */}
				<div className="space-y-4">
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">
							Application ID <span className="text-red-500">*</span>
						</label>
						{inputMode === 'manual' ? (
							<input
								type="text"
								value={applicationId}
								onChange={(e) => setApplicationId(e.target.value)}
								placeholder="550e8400-e29b-41d4-a716-446655440000"
								disabled={isLoading}
								className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 text-sm font-mono"
							/>
						) : (
							<div className="space-y-2">
								{MOCK_IDS.map((mock) => (
									<button
										key={mock.id}
										onClick={() => handleMockSelect(mock.id)}
										disabled={isLoading}
										className={`w-full text-left px-3 py-2 rounded-md border text-sm transition-colors ${
											applicationId === mock.id
												? 'border-blue-500 bg-blue-50 text-blue-800'
												: 'border-gray-200 bg-gray-50 text-gray-700 hover:border-blue-300 hover:bg-blue-50/50'
										} disabled:opacity-50`}>
										<span className="font-mono text-xs block text-gray-500">{mock.id}</span>
										<span className="font-medium">{mock.label}</span>
									</button>
								))}
							</div>
						)}
						{applicationId && (
							<p className="text-xs mt-1 text-gray-500 font-mono">
								Selected: {applicationId}
							</p>
						)}
					</div>

					{/* Simulate Failure Toggle */}
					{
						/**
							 <div className="flex items-center gap-3 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
								<input
									type="checkbox"
									id="simulateFailure"
									checked={simulateFailure}
									onChange={(e) => setSimulateFailure(e.target.checked)}
									disabled={isLoading}
									className="h-4 w-4 text-yellow-600 border-gray-300 rounded focus:ring-yellow-500"
								/>
								<label htmlFor="simulateFailure" className="flex-1">
									<span className="text-sm font-medium text-yellow-800">Simulate Failure</span>
									<span className="block text-xs text-yellow-600">
										Forces the recheck to fail for testing purposes (simulateFailure=true)
									</span>
								</label>
								<span className="text-xs font-mono bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded">
									Dev/Test
								</span>
							</div>
						 *
						 */
					}

					{/* Action Buttons */}
					<div className="flex items-center gap-3 pt-2">
						<button
							onClick={handleSubmit}
							disabled={isLoading || !applicationId.trim()}
							className="px-6 py-2.5 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors text-sm flex items-center gap-2">
							{isLoading ? (
								<>
									<svg
										className="animate-spin h-4 w-4"
										xmlns="http://www.w3.org/2000/svg"
										fill="none"
										viewBox="0 0 24 24">
										<circle
											className="opacity-25"
											cx="12"
											cy="12"
											r="10"
											stroke="currentColor"
											strokeWidth="4"
										/>
										<path
											className="opacity-75"
											fill="currentColor"
											d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
										/>
									</svg>
									Performing Recheck...
								</>
							) : (
								'Run Recheck'
							)}
						</button>
						<button
							onClick={handleReset}
							disabled={isLoading}
							className="px-4 py-2.5 bg-gray-100 text-gray-700 font-medium rounded-md hover:bg-gray-200 disabled:opacity-50 transition-colors text-sm">
							Reset
						</button>
					</div>
				</div>
			</div>

			{/* Validation Error */}
			{error && (
				<div className="bg-red-50 border border-red-200 rounded-lg p-4">
					<p className="text-sm text-red-700">{error}</p>
				</div>
			)}

			{/* API Response */}
			{response && (
				<div className="space-y-4">
					{/* HTTP Status Indicator */}
					<div className="flex items-center gap-2">
						<span
							className={`text-xs font-mono px-2 py-1 rounded ${
								httpStatus === 200
									? 'bg-green-100 text-green-700'
									: httpStatus === 404
									? 'bg-yellow-100 text-yellow-700'
									: 'bg-red-100 text-red-700'
							}`}>
							HTTP {httpStatus}
						</span>
						<span className="text-xs text-gray-400">|</span>
						<span className="text-xs text-gray-500">
							Response received
						</span>
					</div>

					{response.success && response.data ? (
						<RecheckResults data={response.data} />
					) : (
						<ErrorDisplay
							message={response.message || 'Unknown error'}
							errors={response.errors}
							statusCode={httpStatus || undefined}
						/>
					)}
				</div>
			)}

			{/* Reference: Known Failure Codes */}
			<div className="bg-white rounded-lg border shadow-sm p-6">
				<h3 className="text-lg font-semibold text-gray-800 mb-3">Reference: Known Failure Codes</h3>
				<div className="overflow-x-auto">
					<table className="w-full text-sm">
						<thead>
							<tr className="border-b border-gray-200">
								<th className="text-left py-2 pr-4 font-medium text-gray-600">Code</th>
								<th className="text-left py-2 font-medium text-gray-600">Description</th>
							</tr>
						</thead>
						<tbody>
							{Object.entries(FAILURE_CODE_DESCRIPTIONS).map(([code, desc]) => (
								<tr key={code} className="border-b border-gray-100 last:border-0">
									<td className="py-2 pr-4 font-mono text-xs text-blue-700">{code}</td>
									<td className="py-2 text-gray-700">{desc}</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</div>
		</div>
	);
};

export default CreditBureauRecheck;
